import {
  CheckCircleFillIcon,
  DotFillIcon,
  FileDiffIcon,
  GitPullRequestDraftIcon,
  XCircleFillIcon,
  type Icon,
} from '@primer/octicons-react'
import {ConsolidatedReviewState} from '../components/sections/ReviewerSection'
import type {ReviewerSectionStatus} from './merge-box-status-calculator/reviewer-section-status'
import type {ChecksSectionStatus, ChecksSectionStatusType} from './merge-box-status-calculator/checks-section-status'
import type {ConflictsSectionStatus} from './merge-box-status-calculator/conflicts-section-status'
import type {DraftStateSectionStatus} from './merge-box-status-calculator/draft-state-section-status'
import type {ConflictsSectionStatuses} from '../components/sections/ConflictsSection'
import {isSuccessStatus, type StatusChecksPageData} from '../page-data/payloads/status-checks'

export type CondensedStatusRow = {
  icon: Icon
  iconColor: string
  label: string
  detail?: string
}

export function reviewStatusPresentation(reviewerSection: ReviewerSectionStatus): CondensedStatusRow {
  const state = reviewerSection.sectionStatus
  const numRequired = reviewerSection.numReviewsRequired

  switch (state) {
    case ConsolidatedReviewState.APPROVED:
      return {icon: CheckCircleFillIcon, iconColor: 'var(--fgColor-success)', label: 'Approved'}
    case ConsolidatedReviewState.CHANGES_REQUESTED:
      return {icon: FileDiffIcon, iconColor: 'var(--fgColor-danger)', label: 'Changes requested'}
    case ConsolidatedReviewState.REVIEW_REQUIRED:
      return {
        icon: DotFillIcon,
        iconColor: 'var(--fgColor-attention)',
        label: 'Waiting for approvals',
        detail: numRequired > 0 ? `${numRequired} required` : undefined,
      }
    case ConsolidatedReviewState.REVIEW_REQUESTED:
      return {icon: DotFillIcon, iconColor: 'var(--fgColor-attention)', label: 'Review requested'}
    case ConsolidatedReviewState.REVIEWED:
      return {icon: CheckCircleFillIcon, iconColor: 'var(--fgColor-success)', label: 'Reviewed'}
    default:
      return {icon: DotFillIcon, iconColor: 'var(--fgColor-muted)', label: 'Reviews'}
  }
}

function checksCountDetail(statusChecksData: StatusChecksPageData | undefined): string | undefined {
  if (!statusChecksData) return undefined
  const summary = statusChecksData.statusRollup.summary
  if (summary.length === 0) return undefined

  const total = summary.reduce((sum, s) => sum + s.count, 0)
  const passed = summary.filter(s => isSuccessStatus(s.state)).reduce((sum, s) => sum + s.count, 0)
  return `${passed}/${total}`
}

export function checksStatusPresentation(
  checksSection: ChecksSectionStatus,
  statusChecksData: StatusChecksPageData | undefined,
): CondensedStatusRow {
  const state: ChecksSectionStatusType = checksSection.sectionStatus
  const detail = checksCountDetail(statusChecksData)

  switch (state) {
    case 'PASSED':
      return {icon: CheckCircleFillIcon, iconColor: 'var(--fgColor-success)', label: 'All checks passed', detail}
    case 'FAILED':
    case 'SOME_FAILED':
    case 'PENDING_FAILED':
      return {icon: XCircleFillIcon, iconColor: 'var(--fgColor-danger)', label: 'Some checks failing', detail}
    case 'PENDING':
    case 'PENDING_CONFLICTS':
    case 'PENDING_APPROVAL':
      return {icon: DotFillIcon, iconColor: 'var(--fgColor-attention)', label: 'Checks pending', detail}
    default:
      return {icon: DotFillIcon, iconColor: 'var(--fgColor-muted)', label: 'Checks', detail}
  }
}

export function conflictsStatusPresentation(conflictsSection: ConflictsSectionStatus): CondensedStatusRow {
  const state: ConflictsSectionStatuses = conflictsSection.sectionStatus

  switch (state) {
    case 'NO_CONFLICTS':
    case 'HAS_ADVISORY_WORKSPACE':
      return {
        icon: CheckCircleFillIcon,
        iconColor: 'var(--fgColor-success)',
        label: 'No conflicts with base branch',
      }
    case 'HAS_CONFLICTS':
    case 'HAS_REBASE_CONFLICTS':
      return {icon: XCircleFillIcon, iconColor: 'var(--fgColor-danger)', label: 'Merge conflicts'}
    case 'OUT_OF_DATE':
      return {icon: DotFillIcon, iconColor: 'var(--fgColor-attention)', label: 'Branch is out of date'}
    case 'PENDING':
    default:
      return {icon: DotFillIcon, iconColor: 'var(--fgColor-muted)', label: 'Checking for conflicts…'}
  }
}

export function draftStatusPresentation(_draftSection: DraftStateSectionStatus): CondensedStatusRow {
  return {
    icon: GitPullRequestDraftIcon,
    iconColor: 'var(--fgColor-muted)',
    label: 'This pull request is in draft',
  }
}
